import type { Deal, TabState, SCData } from '../types';
import { ALL_DATA } from '../data/deals';
import { median, avgArr } from './format';

export function matchesCQFilter(d: Deal, s: TabState): boolean {
  if (s.fy === 'All' && s.q === 'All') return true;
  const yr = d.created_fy || '';
  const qt = d.created_q || '';
  if (s.fy !== 'All' && yr !== s.fy) return false;
  if (s.q !== 'All' && qt !== s.q) return false;
  return true;
}

export function getFiltered(tab: 'vp' | 'sd' | 'od', s: TabState): Deal[] {
  return ALL_DATA.filter(d => {
    if (s.r && s.r !== 'Global' && d.region !== s.r) return false;
    if (s.sub && s.sub !== 'All' && d.sub_region !== s.sub) return false;
    if (tab === 'vp' && s.status && s.status !== 'All' && d.status !== s.status) return false;
    if (!matchesCQFilter(d, s)) return false;
    return true;
  });
}

export function buildSCList(region: string, sub: string, cqState: TabState): SCData[] {
  const scMap: Record<string, { deals: Deal[]; closed: number; onhold: number; cancelled: number }> = {};

  ALL_DATA.forEach(d => {
    if (region !== 'Global' && d.region !== region) return;
    if (sub && sub !== 'All' && d.sub_region !== sub) return;
    if (!matchesCQFilter(d, cqState)) return;

    if (!scMap[d.sc]) scMap[d.sc] = { deals: [], closed: 0, onhold: 0, cancelled: 0 };
    if (d.status === 'In Progress') scMap[d.sc].deals.push(d);
    else if (d.status === 'Closed') scMap[d.sc].closed++;
    else if (d.status === 'On Hold') scMap[d.sc].onhold++;
    else if (d.status === 'Cancelled') scMap[d.sc].cancelled++;
  });

  return Object.entries(scMap)
    .filter(([, v]) => v.deals.length > 0)
    .map(([sc, v]) => {
      const ages = v.deals.map(d => d.age);
      const aged = v.deals.filter(d => d.age > 90);
      const gnarr = v.deals.reduce((s, d) => s + d.gnarr, 0);
      return {
        n: sc,
        ip: v.deals.length,
        avgAge: avgArr(ages),
        med: median(ages),
        gnarr,
        aged: aged.length,
        riskGnarr: aged.reduce((s, d) => s + d.gnarr, 0),
        deals: v.deals,
        closed: v.closed,
        onhold: v.onhold,
      };
    })
    .sort((a, b) => b.ip - a.ip);
}

export function getFYOptions(): string[] {
  const fySet = new Set(ALL_DATA.map(d => d.created_fy).filter(Boolean));
  return ['All', ...Array.from(fySet).sort()];
}

export const PRODUCT_BUCKETS = [
  { label: 'AEM Assets', test: (p: string) => /(aem.*assets|assets.*aem)/i.test(p) },
  { label: 'Analytics', test: (p: string) => /(d&i.*analytics|analytics)/i.test(p) && !/cja/i.test(p) },
  { label: 'CJA', test: (p: string) => /cja/i.test(p) },
  { label: 'Campaign', test: (p: string) => /campaign/i.test(p) },
  { label: 'Commerce', test: (p: string) => /commerce/i.test(p) },
  { label: 'Marketo', test: (p: string) => /(marketo|measure)/i.test(p) },
  { label: 'Target', test: (p: string) => /target/i.test(p) },
  { label: 'Workfront', test: (p: string) => /(workfront|wkft)/i.test(p) },
];

export function bucketProducts(productsStr: string): Set<string> {
  const bucketHits = new Set<string>();
  const parts = productsStr.split(',').map(s => s.trim());
  for (const part of parts) {
    let matched = false;
    for (const b of PRODUCT_BUCKETS) {
      if (b.test(part)) { bucketHits.add(b.label); matched = true; }
    }
    if (!matched) bucketHits.add('Other');
  }
  return bucketHits;
}
